import { z } from 'zod';

import { captionStyleRefSchema } from '../caption-styles';
import { defineNode, passThrough, total } from '../define';
import { IMAGE_MODEL_IDS } from '../images';
import {
  PERSONA_VARIATION_PROMPT,
  SCENE_FRAME_PROMPT,
  SCENE_MOTION_PROMPT,
} from '../prompts';
import { PLATFORMS } from '../types';
import {
  qualityProblem,
  VIDEO_FORMAT_IDS,
  VIDEO_MODEL_IDS,
  VIDEO_QUALITY_IDS,
} from '../videos';
import { slideSlotSchema } from './starters';

export const HOOK_ANGLES = ['pov', 'question', 'stat', 'boldClaim', 'story'] as const;
export const HELD_CONSTANT = ['visuals', 'voice', 'caption', 'length'] as const;

export const script = defineNode({
  type: 'generate.script',
  version: 1,
  category: 'generate',
  label: 'Script',
  description: 'Rewrite the blueprint in your brand voice — approved claims only.',
  icon: 'PencilSimple',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['blueprint'] }],
  outputs: [{ id: 'out', type: 'script' }],
  mode: 'map',
  executor: 'llm',
  config: z.object({
    length: z.enum(['short', 'medium', 'long']).default('short').meta({
      title: 'Length',
      description: 'Short ≈ 15 s, medium ≈ 30 s, long ≈ 60 s',
      widget: 'segmented',
    }),
    /** Extra direction on top of the brand profile's tone. */
    notes: z
      .string()
      .trim()
      .max(500)
      .optional()
      .meta({ title: 'Direction', widget: 'textarea' }),
  }),
  cardinality: passThrough,
});

export const hookVariations = defineNode({
  type: 'generate.hookVariations',
  version: 1,
  category: 'generate',
  label: 'Hook variations',
  description: 'N opening lines by angle: POV, question, stat, bold claim, story.',
  icon: 'Sparkle',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['script'] }],
  outputs: [{ id: 'out', type: 'script' }],
  mode: 'map',
  executor: 'llm',
  config: z
    .object({
      count: z.int().min(1).max(5).default(5).meta({
        title: 'Variations per script',
        widget: 'segmented',
      }),
      angles: z
        .array(z.enum(HOOK_ANGLES))
        .min(1, 'Pick at least one angle')
        .default([...HOOK_ANGLES])
        .meta({ title: 'Angles', description: 'one per variation', widget: 'chips' }),
      keepConstant: z
        .array(z.enum(HELD_CONSTANT))
        .default(['visuals', 'voice', 'caption'])
        .meta({
          title: 'Keep constant',
          description: 'so results are comparable',
          widget: 'chips',
        }),
    })
    .refine((config) => config.angles.length >= config.count, {
      error: 'Pick an angle for every variation',
      path: ['angles'],
    }),
  cardinality: (config, inputs) => ({ out: total(inputs) * config.count }),
});

export const personaVoice = defineNode({
  type: 'generate.personaVoice',
  version: 1,
  category: 'generate',
  label: 'Persona & voice',
  description: 'Who says it — a persona, AI voiceover or your creator.',
  icon: 'Microphone',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['script'] }],
  // Voice over visuals is a finished reel, so it can go straight to review.
  outputs: [{ id: 'out', type: 'reel' }],
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z
    .object({
      speaker: z.enum(['persona', 'aiVoice', 'creator']).default('persona').meta({
        title: 'Who says it',
        widget: 'segmented',
      }),
      personaId: z.uuid().optional().meta({ title: 'Persona', widget: 'persona' }),
      voiceId: z.string().trim().max(64).optional().meta({
        title: 'Voice',
        description: 'A voice id from your voice provider; empty uses the default voice',
      }),
      visuals: z.enum(['card', 'library']).default('card').meta({
        title: 'Over',
        description: 'A brand card, or b-roll from your library',
        widget: 'segmented',
      }),
      collectionId: z
        .uuid()
        .optional()
        .meta({ title: 'B-roll from', widget: 'collection' }),
    })
    .refine((config) => config.speaker !== 'persona' || config.personaId !== undefined, {
      error: 'Pick a persona',
      path: ['personaId'],
    }),
  cardinality: passThrough,
});

export const visuals = defineNode({
  type: 'generate.visuals',
  version: 1,
  category: 'generate',
  label: 'Visuals',
  description: 'B-roll from your library, stock or AI; swap backgrounds.',
  icon: 'Image',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['script'] }],
  outputs: [{ id: 'out', type: 'clip' }],
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    source: z.enum(['library', 'stock', 'ai']).default('library').meta({
      title: 'From',
      widget: 'segmented',
    }),
    collectionId: z.uuid().optional().meta({ title: 'Collection', widget: 'collection' }),
    replaceBackground: z.boolean().default(false).meta({ title: 'Swap backgrounds' }),
  }),
  cardinality: passThrough,
});

export const carouselSlides = defineNode({
  type: 'generate.carouselSlides',
  version: 1,
  category: 'generate',
  label: 'Carousel slides',
  description: 'Turn a script into 5–10 slides on a brand layout.',
  icon: 'Cards',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['script'] }],
  outputs: [{ id: 'out', type: 'carousel' }],
  mode: 'map',
  // Still images: drawn by the render worker but not charged as a render.
  executor: 'render',
  config: z.object({
    slides: z.int().min(5).max(10).default(7).meta({ title: 'Slides' }),
    background: z.enum(['card', 'ai']).default('card').meta({
      title: 'Background',
      description: 'A plain brand card, or an AI image per slide',
      widget: 'segmented',
    }),
    imageModel: z.enum(IMAGE_MODEL_IDS).optional().meta({
      title: 'AI image model',
      description: 'Default uses your workspace’s model',
      widget: 'select',
    }),
    imageStyle: z.string().trim().max(200).optional().meta({
      title: 'Image style',
      description: 'e.g. soft pastel illustration, moody product photo',
    }),
    personaId: z.uuid().optional().meta({
      title: 'Show a persona',
      description:
        'With AI backgrounds, puts this persona in the images, matching their reference look',
      widget: 'persona',
    }),
  }),
  cardinality: passThrough,
});

/** Persona variations: the small change each shot makes, in order, so shots differ. */
export const SHOT_VARIATIONS = [
  'Shot from slightly lower.',
  'Looking just off-camera.',
  'A softer, closed-mouth smile.',
  'One hand moved up to the hair.',
  'A little closer in, same framing.',
  'Head tilted slightly to the side.',
  'Shot from slightly to the left.',
  'A bigger, laughing smile.',
  'Looking down, then up at the camera mid-glance.',
  'Shoulders turned a little more toward the camera.',
  'Shot from slightly higher.',
  'A little further back, more of the setting in frame.',
  'Shot from slightly to the right.',
  'Eyebrows raised, a surprised look.',
  'Hands together in front.',
  'Weight shifted onto one leg.',
  'A calm, serious expression.',
  'Hair tucked behind one ear.',
  'Chin resting on one hand.',
  'Mid-movement, as if just turning to the camera.',
] as const;

export const personaVariations = defineNode({
  type: 'generate.personaVariations',
  version: 1,
  category: 'generate',
  label: 'Persona variations',
  description:
    'Remake an image with your personas: same scene and pose, their face, a few shots each.',
  icon: 'UserCheck',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['image'] }],
  outputs: [{ id: 'out', type: 'image' }],
  mode: 'map',
  // Still images: made by the image models, not charged as a render.
  executor: 'render',
  // "Every persona with a reference look" depends on the workspace.
  bounded: true,
  config: z.object({
    personaIds: z.array(z.uuid()).default([]).meta({
      title: 'Personas',
      description: 'None ticked: every persona with a reference look',
      widget: 'personaLooks',
    }),
    count: z
      .int()
      .min(1)
      .max(SHOT_VARIATIONS.length)
      .default(10)
      .meta({ title: 'Shots per persona' }),
    imageModel: z.enum(IMAGE_MODEL_IDS).optional().meta({
      title: 'AI image model',
      description: 'Default uses your workspace’s model',
      widget: 'select',
    }),
    aspectRatio: z.enum(['1:1', '4:5', '9:16']).default('4:5').meta({
      title: 'Shape',
      widget: 'segmented',
    }),
    removeText: z.boolean().default(true).meta({
      title: 'Leave out text in the scene',
      description: 'So your captions go on a clean image',
    }),
    prompt: z.string().max(4000).optional().meta({
      title: 'Prompt',
      description: '{variation} becomes each shot’s small change',
      widget: 'prompt',
      prefill: PERSONA_VARIATION_PROMPT,
    }),
  }),
  cardinality: (config, inputs) => ({
    out: total(inputs) * config.count * Math.max(1, config.personaIds.length),
  }),
});

export const sceneVideo = defineNode({
  type: 'generate.sceneVideo',
  version: 1,
  category: 'generate',
  label: 'Scene video',
  description:
    'Turn images into AI videos: a reel’s scenes redrawn with a new person, or any image (one, a collection, persona shots) animated as it is.',
  icon: 'FilmSlate',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['image'] }],
  outputs: [{ id: 'out', type: 'reel' }],
  mode: 'map',
  executor: 'render',
  // A video model's few seconds cost about two renders.
  rendersPerItem: 2,
  config: z
    .object({
      frame: z.enum(['auto', 'redraw', 'asIs']).default('auto').meta({
        title: 'First frame',
        description:
          'Auto redraws a reel’s scenes with a new person and animates any other image as it is',
        widget: 'segmented',
      }),
      videoModel: z.enum(VIDEO_MODEL_IDS).default('kling').meta({
        title: 'AI video model',
        widget: 'segmented',
      }),
      duration: z.enum(['5', '10']).default('5').meta({
        title: 'Length (seconds)',
        widget: 'segmented',
      }),
      format: z.enum(VIDEO_FORMAT_IDS).default('9:16').meta({
        title: 'Format',
        description: 'The video comes out exactly this shape',
        widget: 'segmented',
      }),
      quality: z.enum(VIDEO_QUALITY_IDS).default('high').meta({
        title: 'Quality',
        description: 'Standard 720p · High 1080p · 4K (Kling only)',
        widget: 'segmented',
      }),
      framePrompt: z
        .string()
        .max(4000)
        .optional()
        .meta({
          title: 'First frame prompt',
          description: '{scene} becomes what each scene shows',
          widget: 'prompt',
          prefill: SCENE_FRAME_PROMPT,
          when: { frame: ['auto', 'redraw'] },
        }),
      prompt: z.string().max(4000).optional().meta({
        title: 'How it moves',
        description: '{scene} becomes what each scene shows',
        widget: 'prompt',
        prefill: SCENE_MOTION_PROMPT,
      }),
      imageModel: z
        .enum(IMAGE_MODEL_IDS)
        .optional()
        .meta({
          title: 'AI image model',
          description: 'Redraws the frame. Default uses your workspace’s model',
          when: { frame: ['auto', 'redraw'] },
          widget: 'select',
        }),
    })
    .refine((config) => !qualityProblem(config.videoModel, config.quality), {
      error: '4K is Kling only - pick High or switch to Kling',
      path: ['quality'],
    }),
  cardinality: passThrough,
});

export const buildCarousels = defineNode({
  type: 'generate.buildCarousels',
  version: 1,
  category: 'generate',
  label: 'Build carousels',
  description:
    'Turn incoming images into carousels: a hook caption on each, then your other slides.',
  icon: 'Cards',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['image'] }],
  outputs: [{ id: 'out', type: 'carousel' }],
  // Every image of the run at once, so they can be mixed with the captions.
  mode: 'collect',
  // Composites images: drawn by the render worker but not charged as a render.
  executor: 'render',
  // How many depends on the personas and pools that come in.
  bounded: true,
  config: z.object({
    captionsCollectionId: z.uuid().optional().meta({
      title: 'Hook captions',
      description:
        'Drawn on the hook slide. Leave empty only if your images already carry text',
      widget: 'collection',
    }),
    hookPosition: z.enum(['top', 'center', 'bottom']).default('top').meta({
      title: 'Hook caption position',
      widget: 'segmented',
    }),
    slots: z.array(slideSlotSchema).max(9).default([]).meta({
      title: 'Slides after the hook',
      description:
        'In order, e.g. your app demo. Each takes one image and caption per carousel.',
      widget: 'slots',
    }),
    count: z.int().min(1).max(50).default(10).meta({ title: 'Carousels per persona' }),
    // Unset: the workspace's default style, then White band.
    style: captionStyleRefSchema.optional().meta({
      title: 'Caption style',
      description: 'Used on every slide unless a slide picks its own.',
      widget: 'captionStyle',
    }),
  }),
  cardinality: (config, inputs) => ({ out: total(inputs) ? config.count : 0 }),
});

export const captionHashtags = defineNode({
  type: 'generate.captionHashtags',
  version: 1,
  category: 'generate',
  label: 'Caption & hashtags',
  description: 'Post copy and hashtags tuned per platform.',
  icon: 'Hash',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['reel', 'carousel'] }],
  outputs: [{ id: 'out', type: 'same' }],
  mode: 'map',
  executor: 'llm',
  config: z.object({
    platforms: z.array(z.enum(PLATFORMS)).default([]).meta({
      title: 'Tune for',
      description: 'Empty means every platform you publish to',
      widget: 'chips',
    }),
    hashtags: z.int().min(0).max(30).default(5).meta({ title: 'Hashtags' }),
  }),
  cardinality: passThrough,
});

export const sound = defineNode({
  type: 'generate.sound',
  version: 1,
  category: 'generate',
  label: 'Sound',
  description: 'Your brand track or a rights-checked trending sound.',
  icon: 'MusicNotes',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['reel'] }],
  outputs: [{ id: 'out', type: 'reel' }],
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z
    .object({
      source: z.enum(['brandTrack', 'trending']).default('brandTrack').meta({
        title: 'Sound',
        widget: 'segmented',
      }),
      assetId: z.uuid().optional().meta({ title: 'Track', widget: 'audioTrack' }),
      volume: z.number().min(0.05).max(1).default(0.2).meta({
        title: 'Volume under the voice',
        widget: 'decimal',
      }),
    })
    .refine((config) => config.source !== 'brandTrack' || config.assetId !== undefined, {
      error: 'Pick a track from your library',
      path: ['assetId'],
    }),
  cardinality: passThrough,
});

export const GENERATE = [
  script,
  hookVariations,
  personaVoice,
  visuals,
  carouselSlides,
  personaVariations,
  sceneVideo,
  buildCarousels,
  captionHashtags,
  sound,
];
